import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher, useNavigation } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { getFetcherType } from 'remix-utils/fetcher-type'
import {
	EDIT_ADDRESS_FORM,
	editAddressSchema,
} from '#app/components/forms/edit-address-form/edit-address-form.tsx'
import { type GetAddress } from '#app/models/ecommerce-providers/sylius/shop/fetch/address/get-address.ts'
import { PROFILE_ADDRESSES_ID_I18NEXT } from '#app/routes/($lang).profile.addresses.$id/route-config'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseEditAddressProps = {
	address: GetAddress
}

function useEditAddressForm({ address }: UseEditAddressProps) {
	const { t } = useTranslation(
		PROFILE_ADDRESSES_ID_I18NEXT.routeProfileAddressesId,
	)

	const fetcherKey = EDIT_ADDRESS_FORM.generateFetcherKey!({
		id: address.id!.toString(),
	})
	const fetcher = useFetcher({
		key: fetcherKey,
	})

	const formValues = {
		[ROOT_INPUTS.subaction]: EDIT_ADDRESS_FORM.subaction,
		[ROOT_INPUTS.noJs]: 'true',
	}
	const [form, fields] = useForm({
		lastResult: fetcher.data || undefined,
		defaultValue: {
			...address,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: editAddressSchema })
		},
		shouldValidate: 'onBlur',
	})

	const fetcherType = getFetcherType(fetcher, useNavigation())

	return {
		editAddressFetcherKey: fetcherKey,
		editAddressFetcher: fetcher,
		editAddressFormValues: formValues,
		editAddressForm: form,
		editAddressFields: fields,
		editAddressFetcherType: fetcherType,
		t,
	}
}

export { type UseEditAddressProps, useEditAddressForm }
